<template>
    <div class="mt-4">
        <div class="gap-16 flex">
            <div class="min-w-[400px]">
                <WorkflowStep
                    :assigned-workflows="assignedReservationMadeWorkflows"
                    :unassigned-workflows="unassignedReservationMadeWorkflows"
                    :title="$t('workflows.steps.when_reservation_is_made')"
                    @assign-workflow="(id) => assignWorkflow(id)"
                    @detach-workflow="(id) => detachWorkflow(id)"
                    @toggle-shared="(id) => toggleShared(id)"
                    :integrations="integrations"
                    :trigger="'reservation_made'"
                    :event-type="eventType"
                    :is-owner="isOwner"
                    :can-share-workflows="canShareWorkflows"
                    :current-user-personal-team-id="currentUserPersonalTeamId"
                ></WorkflowStep>

                <WorkflowStep
                    :assigned-workflows="assignedResrvationsCanceledWorkflows"
                    :unassigned-workflows="
                        unassignedResrvationsCanceledWorkflows
                    "
                    :title="$t('workflows.steps.when_reservation_is_canceled')"
                    @assign-workflow="(id) => assignWorkflow(id)"
                    @detach-workflow="(id) => detachWorkflow(id)"
                    @toggle-shared="(id) => toggleShared(id)"
                    :integrations="integrations"
                    :trigger="'reservation_canceled'"
                    :event-type="eventType"
                    :is-owner="isOwner"
                    :can-share-workflows="canShareWorkflows"
                    :current-user-personal-team-id="currentUserPersonalTeamId"
                ></WorkflowStep>

                <WorkflowStep
                    :assigned-workflows="
                        assignedReservationsRescheduledWorkflows
                    "
                    :unassigned-workflows="
                        unassignedReservationsRescheduledWorkflows
                    "
                    :title="
                        $t('workflows.steps.when_reservation_is_rescheduled')
                    "
                    @assign-workflow="(id) => assignWorkflow(id)"
                    @detach-workflow="(id) => detachWorkflow(id)"
                    @toggle-shared="(id) => toggleShared(id)"
                    :integrations="integrations"
                    :trigger="'reservation_rescheduled'"
                    :event-type="eventType"
                    :is-owner="isOwner"
                    :can-share-workflows="canShareWorkflows"
                    :current-user-personal-team-id="currentUserPersonalTeamId"
                ></WorkflowStep>

                <WorkflowStep
                    :assigned-workflows="
                        assignedReservationWillHappenInWorkflows
                    "
                    :unassigned-workflows="
                        unassignedReservationWillHappenInWorkflows
                    "
                    :title="
                        $t('workflows.steps.when_reservation_will_happen_in')
                    "
                    @assign-workflow="(id) => assignWorkflow(id)"
                    @detach-workflow="(id) => detachWorkflow(id)"
                    @toggle-shared="(id) => toggleShared(id)"
                    :integrations="integrations"
                    :is-last="true"
                    :trigger="'reservation_will_happen_in'"
                    :event-type="eventType"
                    :is-owner="isOwner"
                    :can-share-workflows="canShareWorkflows"
                    :current-user-personal-team-id="currentUserPersonalTeamId"
                ></WorkflowStep>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3";
import type { EventType } from "../types/EventType";
import route from "ziggy-js";
import type { Workflow } from "../../Workflows/components/Workflow.vue";
import { computed } from "vue";
import WorkflowStep from "./Workflows/WorkflowStep.vue";

const props = defineProps<{
    eventType: EventType & { workflows: Workflow[] };
    workflows: Workflow[];
    isOwner: boolean;
    canShareWorkflows: boolean;
    currentUserPersonalTeamId: number;
    integrations: any[];
}>();

type SharedData = {
    userId: number;
};

const page = usePage<SharedData>();

const detachWorkflow = function (workflowId: number) {
    if (!props.isOwner) {
        return;
    }

    router.post(
        route("app.event-types.workflows.detach", {
            eventType: props.eventType.id,
        }),
        {
            workflowId: workflowId,
        },
        {
            onFinish: () => {},
        },
    );
};

const assignWorkflow = function (workflowId: number) {
    if (!props.isOwner) {
        return;
    }

    router.post(
        route("app.event-types.workflows.assign", {
            eventType: props.eventType.id,
        }),
        {
            workflowId: workflowId,
        },
        {
            onFinish: () => {},
        },
    );
};

const toggleShared = function (workflowId: number) {
    router.post(
        route("app.event-types.workflows.toggle-shared", {
            eventType: props.eventType.id,
        }),
        {
            workflowId: workflowId,
        },
        {
            onFinish: () => {},
        },
    );
};

const unassignedReservationMadeWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_made" &&
            !isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const assignedReservationMadeWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_made" &&
            isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const assignedResrvationsCanceledWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_canceled" &&
            isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const unassignedResrvationsCanceledWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_canceled" &&
            !isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const assignedReservationWillHappenInWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_will_happen_in" &&
            isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const unassignedReservationWillHappenInWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_will_happen_in" &&
            !isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const assignedReservationsRescheduledWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_rescheduled" &&
            isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const unassignedReservationsRescheduledWorkflows = computed(() => {
    return props.workflows.filter(
        (workflow) =>
            workflow.trigger === "reservation_rescheduled" &&
            !isWorkflowAlreadyAssigned(workflow.id, props.eventType.workflows),
    );
});

const isWorkflowAlreadyAssigned = (id: number, assignedWorkflows: any) => {
    for (let i = 0; i < assignedWorkflows.length; i++) {
        if (assignedWorkflows[i].id === id) {
            if (
                assignedWorkflows[i].pivot.user_id === page.props.userId ||
                assignedWorkflows[i].pivot.user_id === null
            ) {
                return true;
            }
        }
    }
    return false;
};
</script>

<style></style>
